import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getCompanies, getAllCoupons, forceUpdateLineasFariones } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Ticket, LogOut, LayoutDashboard, Store, Tag, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import AdminSidebar from '@/components/AdminSidebar';

const AdminDashboard = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    companies: 0,
    premiumCompanies: 0,
    coupons: 0,
    activeCoupons: 0
  });
  const [loading, setLoading] = useState(true);
  const [forcing, setForcing] = useState(false);
  const [forceResult, setForceResult] = useState(null);

  const handleForceUpdate = async () => {
    if (!window.confirm('¿Forzar actualización de Líneas Romeros y Plus Fariones con los datos del seed? Esto sobreescribirá logo, teléfono, email y cupón PLUSCAMARA.')) {
      return;
    }
    setForcing(true);
    setForceResult(null);
    try {
      const res = await forceUpdateLineasFariones();
      setForceResult({ ok: true, data: res.data });
    } catch (e) {
      setForceResult({ ok: false, error: (e && e.response && e.response.data && e.response.data.detail) || 'Error desconocido' });
    } finally {
      setForcing(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadStats();
  }, [isAuthenticated, navigate]);

  const loadStats = async () => {
    try {
      const [companiesRes, couponsRes] = await Promise.all([
        getCompanies(),
        getAllCoupons()
      ]);
      
      setStats({
        companies: companiesRes.data.length,
        premiumCompanies: companiesRes.data.filter(c => c.is_premium).length,
        coupons: couponsRes.data.length,
        activeCoupons: couponsRes.data.filter(c => c.is_active).length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] grid grid-cols-1 md:grid-cols-[240px_1fr]">
      <AdminSidebar />
      
      <main className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="dashboard-title">
              Panel de Control
            </h1>
            <p className="text-[#64748B] mt-1">
              Gestiona empresas y cupones del Club Cámara
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <StatsCard
              title="Empresas"
              value={stats.companies}
              icon={Building2}
              color="bg-[#d1223b]"
              loading={loading}
            />
            <StatsCard
              title="Cupones Totales"
              value={stats.coupons}
              icon={Ticket}
              color="bg-emerald-500"
              loading={loading}
            />
            <StatsCard
              title="Cupones Activos"
              value={stats.activeCoupons}
              icon={Tag}
              color="bg-violet-500"
              loading={loading}
            />
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-slate-100 hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <div className="w-10 h-10 rounded-lg bg-[#d1223b]/10 flex items-center justify-center">
                    <Store className="w-5 h-5 text-[#d1223b]" />
                  </div>
                  Gestión de Empresas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#64748B] mb-4">
                  Añade, edita o elimina empresas del Club Cámara. Gestiona logos, información de contacto y categorías.
                </p>
                <Link to="/admin/empresas">
                  <Button className="btn-primary" data-testid="manage-companies-btn">
                    Gestionar Empresas
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-slate-100 hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <div className="w-10 h-10 rounded-lg bg-[#fcd605]/20 flex items-center justify-center">
                    <Ticket className="w-5 h-5 text-[#fcd605]" />
                  </div>
                  Gestión de Cupones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#64748B] mb-4">
                  Crea y administra cupones de descuento para las empresas. Añade códigos QR y establece fechas de validez.
                </p>
                <Link to="/admin/cupones">
                  <Button className="btn-accent" data-testid="manage-coupons-btn">
                    Gestionar Cupones
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Maintenance: force sync of Líneas Romeros + Plus Fariones */}
          <Card className="border-slate-100 mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-amber-600" />
                </div>
                Sincronizar Líneas Romeros + Plus Fariones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#64748B] mb-4 text-sm">
                Si los datos de estas empresas no se actualizaron tras el último deploy, pulsa el botón para forzar la sincronización con la última versión del seed (logo, teléfono, email y cupón PLUSCAMARA).
              </p>
              <Button
                onClick={handleForceUpdate}
                disabled={forcing}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="force-update-lineas-fariones-btn"
              >
                {forcing ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sincronizando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Forzar Sincronización
                  </span>
                )}
              </Button>

              {forceResult && forceResult.ok && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2" data-testid="force-update-success">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-600" />
                  <div>
                    <div className="font-semibold mb-1">{forceResult.data.message}</div>
                    <div className="text-xs space-y-0.5">
                      <div>Líneas Romeros: {forceResult.data.lineas_romeros ? forceResult.data.lineas_romeros.name + ' ✓' : 'no encontrada'}</div>
                      <div>Cupón Líneas Romeros: {forceResult.data.lineas_romeros_coupon ? forceResult.data.lineas_romeros_coupon.title + ' ✓' : 'no encontrado'}</div>
                      <div>Plus Fariones: {forceResult.data.plus_fariones ? forceResult.data.plus_fariones.name + ' ✓' : 'no encontrada'}</div>
                      <div>Cupón PLUSCAMARA: {forceResult.data.plus_fariones_coupon ? forceResult.data.plus_fariones_coupon.title + ' ✓' : 'no encontrado'}</div>
                    </div>
                  </div>
                </div>
              )}

              {forceResult && !forceResult.ok && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start gap-2" data-testid="force-update-error">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-600" />
                  <div>
                    <div className="font-semibold mb-1">Error</div>
                    <div className="text-xs">{forceResult.error}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

const StatsCard = ({ title, value, icon: Icon, color, iconColor = 'text-white', loading }) => (
  <Card className="border-slate-100">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[#64748B] mb-1">{title}</p>
          {loading ? (
            <div className="h-8 w-16 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <p className="text-3xl font-bold text-[#0F172A]" data-testid={`stat-${title.toLowerCase().replace(' ', '-')}`}>
              {value}
            </p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default AdminDashboard;
